import { useEffect, useRef, useCallback } from 'react';
import { activityApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * Hook to log page visits for activity tracking
 * @param {string} pageName - Name of the page (e.g., "Dashboard", "Settings")
 */
export function usePageVisitLogger(pageName) {
  const hasLogged = useRef(false);
  
  useEffect(() => {
    // Only log once per page mount
    if (hasLogged.current) return;
    
    // Don't log if no token (not logged in)
    const token = localStorage.getItem('ats_token');
    if (!token) return;
    
    hasLogged.current = true;
    
    // Log the page visit (fire and forget, don't block UI)
    activityApi.logPageVisit(pageName, window.location.pathname).catch(() => {});
  }, [pageName]);
}

/**
 * Function to manually log user actions
 * @param {string} action - Type of action (click, search, filter, submit, etc.)
 * @param {string} entityType - Type of entity (button, form, candidate, job, etc.)
 * @param {string} entityId - Optional ID of the entity
 * @param {string} entityName - Optional name/label of the entity
 * @param {string} details - Optional additional details
 * @param {object} metadata - Optional metadata object
 */
export function logUserAction(action, entityType, entityId = null, entityName = null, details = null, metadata = {}) {
  const token = localStorage.getItem('ats_token');
  if (!token) return Promise.resolve();
  
  // Fire and forget
  return activityApi.log(action, entityType, entityId, entityName, details, metadata).catch(() => {});
}

/**
 * Log a link/button click
 * @param {string} linkName - Name/label of the link clicked
 * @param {string} destination - Where the link goes (URL or action)
 * @param {object} metadata - Optional additional metadata
 */
export function logClick(linkName, destination = null, metadata = {}) {
  return logUserAction('click', 'link', null, linkName, destination, metadata);
}

/**
 * Log a job application
 * @param {string} jobId - ID of the job
 * @param {string} jobTitle - Title of the job
 * @param {string} companyName - Name of the company
 */
export function logJobApplication(jobId, jobTitle, companyName) {
  return logUserAction('applied', 'job', jobId, jobTitle, `Applied to ${jobTitle} at ${companyName}`, {
    company: companyName
  });
}

/**
 * Log a search action
 * @param {string} searchType - Type of search (candidate, job, company)
 * @param {string} query - Search query
 */
export function logSearch(searchType, query) {
  return logUserAction('search', searchType, null, null, `Searched for: ${query}`, { query });
}

/**
 * Log a filter action
 * @param {string} filterType - Type of entity being filtered
 * @param {object} filters - Filter criteria applied
 */
export function logFilter(filterType, filters) {
  const filterStr = Object.entries(filters)
    .filter(([_, v]) => v)
    .map(([k, v]) => `${k}=${v}`)
    .join(', ');
  return logUserAction('filter', filterType, null, null, filterStr, filters);
}

/**
 * Log a candidate view
 * @param {string} candidateId - ID of the candidate
 * @param {string} candidateName - Name of the candidate
 */
export function logCandidateView(candidateId, candidateName) {
  return logUserAction('viewed', 'candidate', candidateId, candidateName, `Viewed candidate: ${candidateName}`);
}

/**
 * Log a job view
 * @param {string} jobId - ID of the job
 * @param {string} jobTitle - Title of the job
 */
export function logJobView(jobId, jobTitle) {
  return logUserAction('viewed', 'job', jobId, jobTitle, `Viewed job: ${jobTitle}`);
}

/**
 * Show activity toast notification
 * @param {string} message - Message to show
 * @param {string} type - Type of toast (success, info, warning)
 */
export function showActivityToast(message, type = 'info') {
  const toastOptions = {
    duration: 3000,
    position: 'bottom-right',
  };
  
  switch (type) {
    case 'success':
      toast.success(message, toastOptions);
      break;
    case 'warning':
      toast.warning(message, toastOptions);
      break;
    case 'error':
      toast.error(message, toastOptions);
      break;
    default:
      toast(message, toastOptions);
  }
}

/**
 * Hook for tracking link clicks with automatic logging
 * Returns a wrapped onClick handler
 */
export function useClickLogger() {
  const trackClick = useCallback((linkName, destination, originalOnClick) => {
    return (e) => {
      // Log the click
      logClick(linkName, destination);
      
      // Call original handler if provided
      if (originalOnClick) {
        originalOnClick(e);
      }
    };
  }, []);
  
  return trackClick;
}

export default usePageVisitLogger;
